import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import * as localStorageService from '../services/local-storage-service';
import { getBookmarks } from '../services/bookmarks-service';
import presentationService from '../services/presentation-service';
import { isPremiumSubscription } from '../services/subscription-check-service';
import bookmarksCheckService, { 
  FREE_PLAN_BOOKMARK_LIMIT,
  getVisibleBookmarks
} from '../services/bookmarks-check-service';
import presentationUsageTracker, { FREE_PLAN_MONTHLY_LIMIT } from '../services/presentation-usage-tracker';
import BookmarkLimitBanner, { HiddenBookmarksWarning } from './BookmarkLimitBanner';
import PresentationLimitBanner from './PresentationLimitBanner';
import { 
  Box, 
  Typography, 
  List, 
  Button, 
  Card, 
  CardContent, 
  Divider,
  Chip,
  IconButton,
  Stack,
  Checkbox,
  Tooltip,
  Alert,
  Snackbar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  CircularProgress,
  LinearProgress
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PresentationIcon from '@mui/icons-material/Slideshow';
import DownloadIcon from '@mui/icons-material/Download';
import LoadingPlaceholder from './LoadingPlaceholder';

export default function Bookmarks() {
  const { user } = useAuth();
  const [bookmarks, setBookmarks] = useState([]);
  const [displayedBookmarks, setDisplayedBookmarks] = useState([]);
  const [isPremium, setIsPremium] = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const [approachingLimit, setApproachingLimit] = useState(false);
  const [presentationUsage, setPresentationUsage] = useState(0);
  const [presentationLimitReached, setPresentationLimitReached] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // State to track which bookmarks are expanded
  const [expandedBookmarks, setExpandedBookmarks] = useState({});
  // State to track selected bookmarks for presentation
  const [selectedBookmarks, setSelectedBookmarks] = useState({});
  // State for presentation generation
  const [generatingPresentation, setGeneratingPresentation] = useState(false);
  // State for notifications
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  // State for presentation name dialog
  const [presentationDialog, setPresentationDialog] = useState({
    open: false,
    name: 'ThinkGPT Bookmarks'
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userData = localStorageService.getUserData();
        const userId = userData?.id;
        if (!userId) throw new Error('User ID not found');
        const result = await getBookmarks(userId);
        const allBookmarks = result.bookmarks || [];
        
        // Check premium status
        const userIsPremium = isPremiumSubscription();
        setIsPremium(userIsPremium);
        
        // Check if approaching or reached limit
        const isReached = bookmarksCheckService.hasReachedBookmarkLimit(allBookmarks);
        const isApproaching = bookmarksCheckService.isApproachingBookmarkLimit(allBookmarks);
        
        setLimitReached(isReached);
        setApproachingLimit(isApproaching);
        
        // Get visible bookmarks based on subscription status
        const { displayedBookmarks: visibleBookmarks } = getVisibleBookmarks(allBookmarks);
        
        // Check presentation limits based on actual presentations in the last 30 days
        const currentPresentationUsage = await presentationUsageTracker.getMonthlyPresentationCount();
        setPresentationUsage(currentPresentationUsage);
        const limitReached = await presentationUsageTracker.hasReachedMonthlyLimit();
        setPresentationLimitReached(limitReached);
        
        setBookmarks(allBookmarks);
        setDisplayedBookmarks(visibleBookmarks);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user]);

  if (loading) return <LoadingPlaceholder />;

  if (error) return (
    <Typography color="error" sx={{ mt: 4 }}>
      Error loading bookmarks: {error}
    </Typography>
  );

  // Toggle expanded state for a bookmark
  const toggleExpand = (bookmarkId) => {
    setExpandedBookmarks(prev => ({
      ...prev,
      [bookmarkId]: !prev[bookmarkId]
    }));
  };
  
  // Function to truncate text
  const truncateText = (text, maxLength = 150) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };
  
  // Toggle bookmark selection
  const toggleBookmarkSelection = (bookmarkId) => {
    setSelectedBookmarks(prev => ({
      ...prev,
      [bookmarkId]: !prev[bookmarkId]
    }));
  };
  
  // Get array of selected bookmarks
  const getSelectedBookmarks = () => {
    return bookmarks.filter(bm => {
      const bookmarkId = bm.id || `bookmark-${bookmarks.indexOf(bm)}`;
      return selectedBookmarks[bookmarkId];
    });
  };
  
  // Check if selected bookmarks exceed the limit
  const isBookmarkLimitExceeded = () => {
    const BOOKMARK_LIMIT = 10;
    return getSelectedBookmarks().length > BOOKMARK_LIMIT;
  };
  
  // Get the maximum number of allowed bookmarks for presentation
  const getBookmarkLimit = () => {
    return FREE_PLAN_BOOKMARK_LIMIT;
  };
  
  // Open the presentation name dialog
  const handleOpenPresentationDialog = () => {
    const selected = getSelectedBookmarks();
    const BOOKMARK_LIMIT = getBookmarkLimit();
    
    if (selected.length === 0) {
      setNotification({
        open: true,
        message: 'Please select at least one bookmark for the presentation',
        severity: 'warning'
      });
      return;
    }
    
    if (selected.length > BOOKMARK_LIMIT) {
      setNotification({
        open: true,
        message: `You can only include up to ${BOOKMARK_LIMIT} bookmarks in a presentation. Please deselect ${selected.length - BOOKMARK_LIMIT} bookmark(s).`,
        severity: 'error'
      });
      return;
    }
    
    // Open the dialog with a default name
    setPresentationDialog({
      open: true,
      name: `ThinkGPT Bookmarks ${new Date().toLocaleDateString()}`
    });
  };
  
  // Handle dialog close
  const handleCloseDialog = () => {
    setPresentationDialog({
      ...presentationDialog,
      open: false
    });
  };
  
  // Handle presentation name change
  const handlePresentationNameChange = (e) => {
    setPresentationDialog({
      ...presentationDialog,
      name: e.target.value
    });
  };
  
  // Handle presentation generation
  const handleGeneratePresentation = async () => {
    try {
      // Check if free user has reached monthly limit
      if (!isPremium) {
        const limitReached = await presentationUsageTracker.hasReachedMonthlyLimit();
        if (limitReached) {
          setNotification({
            open: true,
            message: `You've reached the monthly limit of ${FREE_PLAN_MONTHLY_LIMIT} presentations. Upgrade to premium for unlimited presentations.`,
            severity: 'error'
          });
          return;
        }
      }
      
      // Close the dialog and show the loading indicator
      handleCloseDialog();
      setGeneratingPresentation(true);
      
      // Get selected bookmarks
      const selected = getSelectedBookmarks();
      if (selected.length === 0) {
        throw new Error('No bookmarks selected');
      }
      
      // Generate the presentation
      await presentationService.generatePptxPresentation(selected, presentationDialog.name);
      
      // After successful generation, refresh presentation count
      if (!isPremium) {
        // No need to record separately as the presentation is already saved to Firestore
        // Just refresh the counts from actual presentation data
        const currentMonthUsage = await presentationUsageTracker.getMonthlyPresentationCount();
        setPresentationUsage(currentMonthUsage);
        const limitReached = await presentationUsageTracker.hasReachedMonthlyLimit();
        setPresentationLimitReached(limitReached);
      }
      
      setNotification({
        open: true,
        message: 'PowerPoint presentation generated successfully!',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error generating presentation:', err);
      setNotification({
        open: true,
        message: `Error generating presentation: ${err.message}`,
        severity: 'error'
      });
    } finally {
      setGeneratingPresentation(false);
    }
  };
  
  // Close notification
  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };
  
  return (
    <Box sx={{ mt: 2, px: 2 }}>
      {/* Premium status banners */}
      <BookmarkLimitBanner 
        limitReached={limitReached}
        approaching={approachingLimit}
        totalBookmarks={bookmarks.length}
        displayedBookmarks={displayedBookmarks.length}
      />
      
      {/* Presentation limit banner */}
      <PresentationLimitBanner currentUsage={presentationUsage} />
      
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5">
          Your Bookmarks {!isPremium && bookmarks.length > displayedBookmarks.length && 
            `(Showing ${displayedBookmarks.length} of ${bookmarks.length})`}
        </Typography>
        
        {bookmarks.length > 0 && (
          <Tooltip title={isBookmarkLimitExceeded() ? 
            `Maximum ${getBookmarkLimit()} bookmarks allowed for presentation` : 
            "Generate presentation from selected bookmarks"}>
            <span> {/* Wrapper to handle disabled button tooltip */}
              <Button
                variant="contained"
                color="primary"
                startIcon={<PresentationIcon />}
                endIcon={<DownloadIcon />}
                onClick={handleOpenPresentationDialog}
                disabled={generatingPresentation || getSelectedBookmarks().length === 0 || isBookmarkLimitExceeded() || (!isPremium && presentationLimitReached)}
              >
                {generatingPresentation ? 'Generating...' : 'Generate Presentation'}
              </Button>
            </span>
          </Tooltip>
        )}
      </Stack>
      
      {/* Selection summary */}
      {bookmarks.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {getSelectedBookmarks().length} of {bookmarks.length} bookmarks selected
            {isBookmarkLimitExceeded() && (
              <Typography component="span" color="error" sx={{ ml: 1 }}>
                (Maximum {getBookmarkLimit()} bookmarks allowed for presentation)
              </Typography>
            )}
          </Typography>
        </Box>
      )}
      
      {/* Presentation name dialog */}
      <Dialog open={presentationDialog.open} onClose={handleCloseDialog}>
        <DialogTitle>Name Your Presentation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Enter a name for your presentation. This name will be used for the file name and will be displayed in your presentations list.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="presentation-name"
            label="Presentation Name"
            type="text"
            fullWidth
            variant="outlined"
            value={presentationDialog.name}
            onChange={handlePresentationNameChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleGeneratePresentation} 
            variant="contained" 
            color="primary"
            disabled={!presentationDialog.name.trim()}
          >
            Generate
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Progress dialog */}
      <Dialog
        open={generatingPresentation}
        aria-labelledby="progress-dialog-title"
        PaperProps={{
          style: {
            backgroundColor: 'white',
            boxShadow: 'none',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle id="progress-dialog-title">
          Generating Your Presentation
        </DialogTitle>
        <DialogContent>
          <Box sx={{ width: '100%', mb: 2 }}>
            <LinearProgress color="primary" />
          </Box>
          <DialogContentText>
            Please wait while we create your "{presentationDialog.name}" presentation.
            <br/>
            This may take a few moments depending on the number of bookmarks.
          </DialogContentText>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <CircularProgress size={60} thickness={4} />
          </Box>
        </DialogContent>
      </Dialog>
      
      {/* Notification snackbar */}
      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity} 
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
      
      {bookmarks.length === 0 ? (
        <Typography>No bookmarks found.</Typography>
      ) : (
        <>
          <List sx={{ width: '100%' }}>
            {displayedBookmarks.map((bm, idx) => {
              // Generate a stable ID for the bookmark
              const bookmarkId = bm.id || `bookmark-${idx}`;
              const isExpanded = expandedBookmarks[bookmarkId];
            
            return (
              <Card key={bookmarkId} sx={{ mb: 2, boxShadow: 2 }}>
                <CardContent>
                  {/* Header with checkbox, platform and open link button */}
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {/* Selection checkbox in its own column */}
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Checkbox
                          checked={!!selectedBookmarks[bookmarkId]}
                          onChange={() => toggleBookmarkSelection(bookmarkId)}
                          sx={{ 
                            padding: '4px',
                            '& .MuiSvgIcon-root': { fontSize: 20 }
                          }}
                        />
                      </Box>
                      
                      {/* Platform chip */}
                      {bm.platform && (
                        <Chip 
                          label={bm.platform} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                          sx={{ textTransform: 'capitalize' }} 
                        />
                      )}
                    </Stack>
                    
                    {/* URL button */}
                    <IconButton 
                      size="small" 
                      color="primary" 
                      component="a" 
                      href={bm.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      title="Open chat"
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                  
                  {/* Display the bookmark text content - this is the main focus */}
                  <Box sx={{ mt: 1 }}>
                    {bm.textContent ? (
                      <>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                          {isExpanded ? bm.textContent : truncateText(bm.textContent)}
                        </Typography>
                        
                        {bm.textContent.length > 150 && (
                          <Button 
                            onClick={() => toggleExpand(bookmarkId)} 
                            sx={{ mt: 1 }}
                            size="small"
                            variant="outlined"
                          >
                            {isExpanded ? 'See Less' : 'See More'}
                          </Button>
                        )}
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        No text content available for this bookmark
                      </Typography>
                    )}
                  </Box>
                  
                  <Divider sx={{ my: 1.5 }} />
                  
                  {/* Footer with metadata */}
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      ID: {bm.messageId ? bm.messageId.substring(0, 8) + '...' : 'N/A'}
                    </Typography>
                    
                    {bm.lastSyncTime && (
                      <Typography variant="caption" color="text.secondary">
                        Synced: {new Date(bm.lastSyncTime._seconds * 1000).toLocaleDateString()}
                      </Typography>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            );
            })}
          </List>
          
          {/* Show warning about hidden bookmarks if applicable */}
          {!isPremium && bookmarks.length > displayedBookmarks.length && (
            <HiddenBookmarksWarning 
              totalBookmarks={bookmarks.length}
              displayedBookmarks={displayedBookmarks.length}
              limit={FREE_PLAN_BOOKMARK_LIMIT}
            />
          )}
        </>
      )}
    </Box>
  );
}
